import { JsonApiRelationshipMetadata } from '../interfaces';
import { JsonApiDocumentInterface } from '../interfaces/json-api-document';
import { JsonApiError } from '../interfaces/json-api-error';
import { JsonApiIdentifierInterface } from '../interfaces/json-api-identifier-interface';
import { JsonApiImplementation } from '../interfaces/json-api-implementation';
import { JsonApiLinks } from '../interfaces/json-api-links';
import { JsonApiMeta } from '../interfaces/json-api-meta';
import { JsonApiResourceInterface } from '../interfaces/json-api-resource-interface';
import { getResourceType } from '../utils/get-resource-type';
import { ResourceType } from '../utils/resource-type';
import { JsonApiIdentifier } from './json-api-identifier';
import { JsonApiResource } from './json-api-resource';

export abstract class JsonApiDocument<
  T extends
    | JsonApiResource
    | JsonApiResource[]
    | JsonApiIdentifier
    | JsonApiIdentifier[]
> implements JsonApiDocumentInterface
{
  get data(): T {
    if (this._data) {
      return this._data;
    }
    throw new Error('Missing data');
  }
  protected _data?: T;

  get included(): JsonApiResource[] {
    return this._included || [];
  }
  protected _included?: JsonApiResource[];

  readonly errors?: JsonApiError[];
  readonly meta?: JsonApiMeta;
  readonly links?: JsonApiLinks;
  readonly jsonapi?: JsonApiImplementation;

  private get allResources(): JsonApiIdentifierInterface[] {
    return (this.included as JsonApiIdentifierInterface[])?.concat(
      this.data || []
    );
  }

  constructor(document: JsonApiDocumentInterface) {
    this.meta = document.meta;
    this.links = document.links;
    this.jsonapi = document.jsonapi;
    this.errors = document.errors;
  }

  public find<R extends JsonApiResource>({
    type,
    id,
  }: JsonApiIdentifierInterface): R | undefined {
    return this.allResources.find(
      (resource) => resource.type === type && resource.id === id
    ) as R;
  }

  public findAll<R extends JsonApiResource>({
    type,
    id,
  }: JsonApiIdentifierInterface): R[] {
    return this.allResources.filter(
      (resource) => resource.type === type && resource.id === id
    ) as R[];
  }

  protected createResource<R extends JsonApiResource>(
    data: JsonApiResourceInterface
  ): R {
    const resourceType = getResourceType(data.type) as ResourceType<R>;
    return new resourceType(data);
  }

  protected populateResources(document: JsonApiDocumentInterface): void {
    if (document.included) {
      const definitions = document.included.concat(document.data || []);

      definitions.forEach((definition) => {
        const relationships = definition.relationships || {};
        Object.entries(relationships).forEach(([field, { data }]) => {
          if (data !== null && data !== undefined) {
            const resources = this.findAll(definition);
            resources.forEach((resource) => {
              const relationshipsMeta = Reflect.getMetadata(
                'relationships',
                resource
              ) as JsonApiRelationshipMetadata[];
              const relationshipMeta = relationshipsMeta.find(
                (meta) => meta.key === field
              );
              if (!relationshipMeta) {
                throw new Error(
                  `Missing relationship meta for ${resource.type}.${field}`
                );
              }
              (resource as Record<string, any>)[relationshipMeta.propertyName] =
                data instanceof Array
                  ? data.map((item) => this.find(item))
                  : this.find(data);
            });
          }
        });
      });
    }
  }
}
